import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { DashboardNarrativeContent } from "src/components/common";
import { getApiLangFromI18n, type ApiLang } from "src/utils/apiLang";
import { useAiSummaryToggle, type AiSummaryToggleControl } from "./useAiSummaryToggle";
import {
  fetchDashboardNarrative,
  type DashboardNarrativeInsight,
  type DashboardNarrativeKey,
  type DashboardNarrativePayload,
  type DashboardNarrativeResponse,
  type DashboardNarrativeSegment,
  type DashboardNarrativeSentiment,
} from "src/services/DashboardNarrativeAPI";

export type { DashboardNarrativeKey } from "src/services/DashboardNarrativeAPI";

const NARRATIVE_LIMIT_REACHED_CODE = "NARRATIVE_LIMIT_REACHED";
const narrativeResponseCache = new Map<string, DashboardNarrativeResponse>();
const narrativeRequestInFlight = new Map<string, Promise<DashboardNarrativeResponse>>();

interface UseDashboardNarrativeParams {
  dashboardKey: DashboardNarrativeKey;
  tabKey?: string;
  startDate?: string;
  endDate?: string;
  data: Record<string, unknown>;
  enabled?: boolean;
  promptVersion?: string;
  forceRefresh?: boolean;
}

const createPayload = (
  {
    dashboardKey,
    tabKey,
    data,
    promptVersion = "v3",
    forceRefresh = false,
  }: UseDashboardNarrativeParams,
  lang: ApiLang,
): DashboardNarrativePayload => {
  const payload: DashboardNarrativePayload = {
    dashboard_key: dashboardKey,
    ...(tabKey ? { tab_key: tabKey } : {}),
    data,
    prompt_version: promptVersion,
    force_refresh: forceRefresh,
    lang,
  };
  return payload;
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" &&
  value !== null &&
  !Array.isArray(value);

const stableStringify = (value: unknown): string => {
  if (Array.isArray(value)) {
    return `[${value.map(item => stableStringify(item)).join(",")}]`;
  }

  if (isPlainObject(value)) {
    return `{${Object.keys(value)
      .sort()
      .map(key => `${JSON.stringify(key)}:${stableStringify(value[key])}`)
      .join(",")}}`;
  }

  return JSON.stringify(value) ?? "undefined";
};

const normalizeNarrativeText = (value: string | undefined) => (value ?? "").trim();

const normalizeNarrativeList = (value: string[] | undefined) =>
  (value ?? [])
    .map(item => item.replace(/^[\s*-]+/, "").trim())
    .filter(Boolean);

const normalizeNarrativeSegments = (
  value: DashboardNarrativeResponse["narrative"]["summary_segments"] | undefined,
): DashboardNarrativeSegment[] =>
  (value ?? [])
    .map((segment): DashboardNarrativeSegment | null => {
      const text = normalizeNarrativeText(segment.text);
      if (!text) {
        return null;
      }

      const type: DashboardNarrativeSegment["type"] =
        segment.type === "positive" || segment.type === "negative" ? segment.type : "text";

      return { text, type };
    })
    .filter((segment): segment is DashboardNarrativeSegment => Boolean(segment));

const normalizeNarrativeSentiment = (value: string | undefined): DashboardNarrativeSentiment => {
  if (value === "positive" || value === "negative") {
    return value;
  }

  return "neutral";
};

const normalizeNarrativeInsights = (
  value: DashboardNarrativeResponse["narrative"]["key_insights"] | undefined,
): DashboardNarrativeContent["keyInsights"] =>
  (value ?? [])
    .map((item): string | DashboardNarrativeInsight | null => {
      if (typeof item === "string") {
        const text = normalizeNarrativeText(item);
        return text ? text : null;
      }

      const text = normalizeNarrativeText(item.text);
      if (!text) {
        return null;
      }

      return {
        text,
        sentiment: normalizeNarrativeSentiment(item.sentiment),
      } satisfies DashboardNarrativeInsight;
    })
    .filter((item): item is string | DashboardNarrativeInsight => Boolean(item));

const getErrorCode = (err: unknown): string | undefined => {
  if (!(err instanceof Error) || !("code" in err)) {
    return undefined;
  }

  const code = (err as { code?: unknown }).code;
  return typeof code === "string" ? code : undefined;
};

const isNarrativeLimitError = (err: unknown, message: string) =>
  getErrorCode(err) === NARRATIVE_LIMIT_REACHED_CODE ||
  message.toLowerCase().includes("limit");

const mapNarrativeResponse = (
  response: DashboardNarrativeResponse | null,
): DashboardNarrativeContent | null => {
  if (!response?.narrative) {
    return null;
  }

  const summary = normalizeNarrativeText(response.narrative.summary);
  const summarySegments = normalizeNarrativeSegments(response.narrative.summary_segments);
  const keyInsights = normalizeNarrativeInsights(response.narrative.key_insights);
  const recommendations = normalizeNarrativeList(response.narrative.recommended_actions);

  if (!summary && !summarySegments.length && !keyInsights?.length && !recommendations.length) {
    return null;
  }

  return {
    summary: summary || summarySegments.map(segment => segment.text).join(" "),
    summarySegments: summarySegments.length ? summarySegments : undefined,
    keyInsights,
    recommendations,
  };
};

export function useDashboardNarrative(params: UseDashboardNarrativeParams) {
  const {
    dashboardKey,
    tabKey,
    data,
    enabled = true,
    promptVersion = "v3",
    forceRefresh = false,
  } = params;
  const { i18n } = useTranslation();
  const lang = getApiLangFromI18n(i18n);
  const aiSummary = useAiSummaryToggle(dashboardKey);
  const [response, setResponse] = useState<DashboardNarrativeResponse | null>(null);
  const [fallbackNarrative, setFallbackNarrative] = useState<DashboardNarrativeContent | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);


  const generationAllowed =
    enabled && aiSummary.ready && aiSummary.featureAvailable && aiSummary.enabled;

  const payload = useMemo(
    () =>
      createPayload(
        {
          dashboardKey,
          tabKey,
          data,
          promptVersion,
          forceRefresh,
        },
        lang,
      ),
    // `lang` is part of the payload, so `payloadKey` changes with the locale and the
    // narrative is refetched (rather than served from another language's cache entry).
    [dashboardKey, data, forceRefresh, lang, promptVersion, tabKey],
  );
  const payloadKey = useMemo(() => stableStringify(payload), [payload]);
  const payloadRef = useRef(payload);
  payloadRef.current = payload;

  useEffect(() => {
    if (!generationAllowed) {
      setResponse(null);
      setFallbackNarrative(null);
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setFallbackNarrative(null);

    const currentPayload = payloadRef.current;
    const cachedResponse = narrativeResponseCache.get(payloadKey);
    if (cachedResponse) {
      setResponse(cachedResponse);
      setFallbackNarrative(null);
      setLoading(false);
      return;
    }

    const request =
      narrativeRequestInFlight.get(payloadKey) ??
      fetchDashboardNarrative(currentPayload).finally(() => {
        narrativeRequestInFlight.delete(payloadKey);
      });

    if (!narrativeRequestInFlight.has(payloadKey)) {
      narrativeRequestInFlight.set(payloadKey, request);
    }

    request
      .then(result => {
        narrativeResponseCache.set(payloadKey, result);
        if (!cancelled) {
          setResponse(result);
          setFallbackNarrative(null);
        }
      })
      .catch(err => {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : "Failed to load narrative";
          setResponse(null);
          setError(message);
          if (isNarrativeLimitError(err, message)) {
            setFallbackNarrative({
              status: "limit_reached",
              summary: "",
              limitMessage: message,
            });
          }
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [generationAllowed, payloadKey]);

  const narrative = useMemo(
    () => fallbackNarrative ?? mapNarrativeResponse(response),
    [fallbackNarrative, response],
  );

  return {
    narrative,
    response,
    loading: loading || (enabled && aiSummary.loading),
    error,
    payload,
    aiSummary,
  };
}

export type { AiSummaryToggleControl };
